import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Pagination, Spin, Table, Tooltip } from "antd";

import { AlertTriangle, ArrowRight, Check, Download, Eye, FileText, Flag, ListChecks, RefreshCw, UserRound, X, CheckCircle2, History } from "lucide-react";

import LoadingOverlay from "../shared/LoadingOverlay";
import Toast from "../shared/Toast";
import RulesValidationModalV2 from "../cuReview/RulesValidationModalV2";
import "../loanapproval/LoanApprovalPage.css";
import { getAccessToken, getServiceAccessToken } from "../../services/authApi";

import {
    getLoan,
    fieldReader,
    fetchLoanMilestones,
    fetchLoanConditions,
    addLoanCondition,
    addConditionTracking,
    updateLoanMilestone,
    checkLoanResourceLock
} from "../../services/loanApi";
import { getUserByUserId } from "../../services/userApi";
import { fetchAllApprovalLetters as fetchAllApprovalLettersFromApi, downloadApprovalCertificate as downloadApprovalCertificateFromApi } from "../../services/documentApi";
import { runPreMilestoneCompletionRules } from "./ruleExecutionUtils";

import { generateAdditionalDocuments } from "../approvalcertificate/approvalCertificateHelper";
import { error as logAppInsightError } from "../../utils/logger";
import { logError } from "../../utils/logError";

import {
    getLoanApprovalDecision,
    findMatchingLoanApprovalRule,
    getLockValidationStatus,
    getOpenPTDStatus,
    normalizeMilestoneName,
    getActivePTDs,
    getLockValidationCondition,
    isConditionClearedByTracking,
    findMilestoneByName,
    getApprovalAttachmentType,
    groupValidationMessages,
    sanitizeFileName,
    getCreatedDateDisplay,
    getCurrentVersionState,
    getApprovalCreatedTimestamp,
    CONDITION_TITLE_LOCK_VALIDATION,
    MILESTONE_SUBMIT_FOR_CONDITION_REVIEW,
    MILESTONE_CONDITIONAL_APPROVAL,
    MILESTONE_SUBMIT_FOR_FINAL_REVIEW,
    MILESTONE_PROGRESS_ORDER,
    MILESTONES,
    POST_CTC_MILESTONES,
    UW_MANAGER_RETURN_MILESTONES,
    WORKFLOW_TYPES
} from "../../config/loanApprovalConfig";

import { fetchMilestonePersonaPermission } from "../../services/personaApi";

import { LOAN_APPROVAL_PAGE_NAME, getMilestonePersonaAccess } from "../../utils/loanApprovalPersonaAccess";

import {
    API_DOMAIN,
    LOCAL_LOAN_ID,
    LOCAL_USER_ID,
    getEncompassAuthCode,
    getEncompassCurrentUser,
    isLocalEncompassMode,
    getEncompassAuthObject
} from "../../services/interceptors/elliemaeApi";

import * as pdfjsLib from "pdfjs-dist";
import "pdfjs-dist/legacy/build/pdf.worker.min.mjs";

pdfjsLib.GlobalWorkerOptions.workerSrc = "";

const LOAN_APPROVAL_FAILURE_EVENT_TYPES = {
    CREATE_CONDITION_FAILED: "loanapproval-create-condition",
    CLEAR_CONDITION_STATUS_FAILED: "loanapproval-clear-condition-status",
    APPROVE_API_FAILED: "loanapproval-approve"
};

export default function LoanApprovalMilestoneRuleExecutionPage() {
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [loadingText, setLoadingText] = useState("");
    const [progress, setProgress] = useState(10);

    const [loanId, setLoanId] = useState("");
    const [loanType, setLoanType] = useState("");
    const [currentMilestone, setCurrentMilestone] = useState("");
    const [rateLockStatus, setRateLockStatus] = useState("");
    const [user, setUser] = useState(null);

    const [personaPermission, setPersonaPermission] = useState(null);

    const [personaPermissionStatus, setPersonaPermissionStatus] = useState("loading");

    const [personaPermissionError, setPersonaPermissionError] = useState("");

    const [conditions, setConditions] = useState([]);
    const [milestones, setMilestones] = useState([]);

    const [error, setError] = useState("");

    const [approvalAttachments, setApprovalAttachments] = useState([]);

    const [attachmentsLoading, setAttachmentsLoading] = useState(false);

    const [approvalPage, setApprovalPage] = useState(1);

    const [approvalPageSize, setApprovalPageSize] = useState(10);

    const [loanLocked, setLoanLocked] = useState(false);
    const [lockedByUser, setLockedByUser] = useState("");

    const [previewVisible, setPreviewVisible] = useState(false);

    const [approveConfirmationVisible, setApproveConfirmationVisible] = useState(false);

    const [previewLoading, setPreviewLoading] = useState(false);

    const [previewTitle, setPreviewTitle] = useState("");

    const [pdfPages, setPdfPages] = useState([]);

    const [toastVisible, setToastVisible] = useState(false);

    const [toastType, setToastType] = useState("success");

    const [toastMessage, setToastMessage] = useState("");

    const [rulesModalOpen, setRulesModalOpen] = useState(false);
    const [rulesModalResult, setRulesModalResult] = useState(null);
    const [pendingRuleGateAction, setPendingRuleGateAction] = useState(null);

    const normalizedCurrentMilestone = useMemo(() => normalizeMilestoneName(currentMilestone), [currentMilestone]);

    const activePTDs = useMemo(() => getActivePTDs(conditions), [conditions]);

    const [validationMessages, setValidationMessages] = useState([]);

    const hasOpenPTD = activePTDs.length > 0;
    const openPTDCount = activePTDs.length;

    const [pageRefreshing, setPageRefreshing] = useState(false);

    const milestonePersonaAccess = useMemo(
        () =>
            getMilestonePersonaAccess({
                user,
                currentMilestone: normalizedCurrentMilestone,
                permissionConfig: personaPermission
            }),
        [user, normalizedCurrentMilestone, personaPermission]
    );

    const workflowPermissionLoaded = personaPermissionStatus === "loaded";

    const hasWorkflowPermission = workflowPermissionLoaded && milestonePersonaAccess.hasAccess;

    const currentMilestonePermission = useMemo(() => {
        const milestonePermissions = Array.isArray(personaPermission?.milestonePermissions) ? personaPermission.milestonePermissions : [];

        return milestonePermissions.find(milestonePermission => normalizeMilestoneName(milestonePermission?.milestoneName) === normalizedCurrentMilestone);
    }, [personaPermission, normalizedCurrentMilestone]);

    const decision = useMemo(
        () =>
            getLoanApprovalDecision({
                currentMilestone: normalizedCurrentMilestone,
                workflowType: hasWorkflowPermission ? milestonePersonaAccess.workflowType : "",
                rateLockStatus,
                hasOpenPTD,
                loanApprovalRules: currentMilestonePermission?.loanApprovalRules
            }),
        [normalizedCurrentMilestone, hasWorkflowPermission, milestonePersonaAccess.workflowType, rateLockStatus, hasOpenPTD, currentMilestonePermission]
    );

    const lockValidationDisplay = useMemo(() => getLockValidationStatus(rateLockStatus), [rateLockStatus]);

    const openPTDDisplay = useMemo(() => getOpenPTDStatus(hasOpenPTD), [hasOpenPTD]);

    const conditionalApprovalCompleted = useMemo(() => {
        const conditionalApprovalMilestone = findMilestoneByName(milestones, MILESTONE_CONDITIONAL_APPROVAL);

        return conditionalApprovalMilestone?.doneIndicator === true;
    }, [milestones]);

    const ctcCompleted = useMemo(() => findMilestoneByName(milestones, MILESTONES.CTC)?.doneIndicator === true, [milestones]);

    const showUnderwriterWorkflow =
        hasWorkflowPermission &&
        [WORKFLOW_TYPES.UNDERWRITER, WORKFLOW_TYPES.COLLATERAL_UNDERWRITER, WORKFLOW_TYPES.UW_MANAGER].includes(milestonePersonaAccess.workflowType) &&
        decision?.isSupported;

    const isUwManagerReturnWorkflow =
        hasWorkflowPermission &&
        milestonePersonaAccess.workflowType === WORKFLOW_TYPES.UW_MANAGER &&
        UW_MANAGER_RETURN_MILESTONES.includes(normalizedCurrentMilestone) &&
        decision?.milestoneAction === "reopen" &&
        decision?.targetMilestone === MILESTONE_SUBMIT_FOR_FINAL_REVIEW;

    const showAccountManagerWorkflow = hasWorkflowPermission && milestonePersonaAccess.workflowType === WORKFLOW_TYPES.ACCOUNT_MANAGER;

    const showWorkflow = showUnderwriterWorkflow || showAccountManagerWorkflow;

    const progressTargetMilestone = decision?.targetMilestone || normalizedCurrentMilestone;

    const postCtcCurrentMilestone = ctcCompleted && POST_CTC_MILESTONES.includes(normalizedCurrentMilestone) ? normalizedCurrentMilestone : "";

    const progressMilestoneOrder = useMemo(
        () => (postCtcCurrentMilestone ? [...MILESTONE_PROGRESS_ORDER, postCtcCurrentMilestone] : MILESTONE_PROGRESS_ORDER),
        [postCtcCurrentMilestone]
    );

    const progressCurrentMilestone = postCtcCurrentMilestone || normalizedCurrentMilestone;

    const sortedApprovalAttachments = useMemo(() => {
        return [...approvalAttachments].sort((left, right) => {
            const leftIsCurrent = getCurrentVersionState(left?.currentVersion ?? left?.isActive) === true;

            const rightIsCurrent = getCurrentVersionState(right?.currentVersion ?? right?.isActive) === true;

            if (leftIsCurrent !== rightIsCurrent) {
                return rightIsCurrent ? 1 : -1;
            }

            return getApprovalCreatedTimestamp(right) - getApprovalCreatedTimestamp(left);
        });
    }, [approvalAttachments]);

    const showToast = useCallback((message, type = "success") => {
        setToastMessage(message);
        setToastType(type);
        setToastVisible(true);

        window.setTimeout(() => {
            setToastVisible(false);
        }, 5000);
    }, []);

    const fetchApprovalCertificateGrid = useCallback(async () => {
        if (!loanId) {
            return;
        }

        setAttachmentsLoading(true);

        try {
            const authCode = await getEncompassAuthCode();

            const result = await fetchAllApprovalLettersFromApi(authCode, loanId);

            const attachments = Array.isArray(result) ? result : result?.attachments || result?.data || [];

            setApprovalAttachments(Array.isArray(attachments) ? attachments : []);
        } catch (err) {
            const errorCode = logError("LOAN_APPROVAL_FETCH_ATTACHMENT_ERROR");

            logAppInsightError("Loan Approval Attachment Fetch Error", {
                component: "LoanApprovalPage",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed to fetch approval letters.`
            });

            console.error("LoanApprovalPage: fetchApprovalCertificateGrid error:", err);

            showToast("Failed to fetch the approval letter list.", "error");
        } finally {
            setAttachmentsLoading(false);
        }
    }, [loanId, showToast]);

    useEffect(() => {
        initializePage();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!loanId) {
            return;
        }

        fetchApprovalCertificateGrid();
        checkIfLoanLocked();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loanId, fetchApprovalCertificateGrid]);

    async function loadLoanApprovalPersonaPermission() {
        setPersonaPermissionStatus("loading");
        setPersonaPermissionError("");
        setPersonaPermission(null);

        try {
            const permission = await fetchMilestonePersonaPermission(LOAN_APPROVAL_PAGE_NAME);

            if (!permission || permission.pageName !== LOAN_APPROVAL_PAGE_NAME || !Array.isArray(permission.milestonePermissions)) {
                throw new Error("Loan Approval configuration response is invalid.");
            }

            setPersonaPermission(permission);
            setPersonaPermissionStatus("loaded");

            return permission;
        } catch (err) {
            console.error("LoanApprovalPage: loadLoanApprovalPersonaPermission failed", err);

            setPersonaPermission(null);
            setPersonaPermissionStatus("failed");
            setPersonaPermissionError("Workflow permissions could not be loaded. Workflow actions are disabled.");

            return null;
        }
    }

    async function getLoanConditionServiceAccessToken() {
        const serviceAuthCode = await getEncompassAuthCode();

        const serviceAccessToken = await getServiceAccessToken(serviceAuthCode);

        if (!serviceAccessToken) {
            throw new Error("Service access token could not be obtained for loan conditions.");
        }

        return serviceAccessToken;
    }

    async function fetchLoanConditionsWithServiceToken(currentLoanId) {
        const serviceAccessToken = await getLoanConditionServiceAccessToken();

        return fetchLoanConditions(serviceAccessToken, currentLoanId);
    }

    async function initializePage() {
        setLoading(true);
        setError("");

        try {
            const localMode = isLocalEncompassMode();

            if (!localMode) {
                if (!window.elli?.script?.guest) {
                    throw new Error("elli.script.guest is not available.");
                }

                window.elli.script.guest.create();
            }

            const authCode = await getEncompassAuthCode();
            const accessToken = await getAccessToken(authCode);

            let currentLoanId = "";
            let loan = {};
            let currentUser = null;

            if (localMode) {
                if (!LOCAL_LOAN_ID) {
                    throw new Error("REACT_APP_LOCAL_LOAN_ID is not configured.");
                }

                if (!LOCAL_USER_ID) {
                    throw new Error("REACT_APP_LOCAL_USER_ID is not configured.");
                }

                currentLoanId = LOCAL_LOAN_ID;

                const loanPayload = await getLoan(accessToken, currentLoanId);

                loan = loanPayload?.loan || loanPayload || {};

                currentUser = await getUserByUserId(accessToken, LOCAL_USER_ID);
            } else {
                const loanObject = await window.elli.script.getObject("loan");

                const loanPayload = await loanObject.all();

                loan = loanPayload?.loan || loanPayload || {};

                currentLoanId = loan?.id || (await loanObject.getField("GUID"));

                currentUser = await getEncompassCurrentUser();
            }

            if (!currentLoanId) {
                throw new Error("Loan ID could not be determined.");
            }

            if (!loan) {
                throw new Error(`Loan "${currentLoanId}" could not be loaded.`);
            }

            if (!currentUser) {
                throw new Error(localMode ? `User "${LOCAL_USER_ID}" could not be loaded.` : "The current Encompass user could not be loaded.");
            }

            const fields = ["364", "Log.MS.CurrentMilestone", "4788", "CX.LOAN.TYPE"];

            const [fieldData] = await Promise.all([fieldReader(accessToken, fields, currentLoanId), loadLoanApprovalPersonaPermission()]);

            const currentMilestoneValue = (fieldData["Log.MS.CurrentMilestone"] || "").trim();

            const lockStatusFromField = (fieldData["4788"] || "").trim();

            const lockStatusFromLoan = (loan?.rateLock?.lockValidationStatus || "").trim();

            const [milestoneData, conditionData] = await Promise.all([fetchLoanMilestones(accessToken, currentLoanId), fetchLoanConditionsWithServiceToken(currentLoanId)]);

            setLoanId(currentLoanId);
            setLoanType(fieldData["CX.LOAN.TYPE"] || "");
            setCurrentMilestone(currentMilestoneValue);
            setRateLockStatus(lockStatusFromField || lockStatusFromLoan);
            setUser(currentUser);
            setMilestones(milestoneData || []);
            setConditions(conditionData || []);

            await syncHostLoanWorkspace();
        } catch (err) {
            const errorCode = logError("LOAN_APPROVAL_PAGE_INIT_ERROR");

            logAppInsightError("Loan Approval Page Init Error", {
                component: "LoanApprovalPage",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed to load Loan Approval page.`
            });

            console.error("LoanApprovalPage: initializePage failed", err);

            setError(err?.message || "Failed to load Loan Approval details.");
        } finally {
            setLoading(false);
        }
    }

    async function refreshLoanApprovalPageData() {
        const authCode = await getEncompassAuthCode();

        const token = await getAccessToken(authCode);

        const fields = ["364", "Log.MS.CurrentMilestone", "4788", "CX.LOAN.TYPE"];

        const [fieldData, milestoneData, conditionData] = await Promise.all([
            fieldReader(token, fields, loanId),
            fetchLoanMilestones(token, loanId),
            fetchLoanConditionsWithServiceToken(loanId)
        ]);

        setCurrentMilestone((fieldData["Log.MS.CurrentMilestone"] || "").trim());

        setRateLockStatus((fieldData["4788"] || "").trim());

        setLoanType(fieldData["CX.LOAN.TYPE"] || "");

        setMilestones(milestoneData || []);
        setConditions(conditionData || []);

        await fetchApprovalCertificateGrid();
    }
    async function handlePageRefresh() {
        if (!loanId || pageRefreshing) {
            return;
        }

        try {
            setPageRefreshing(true);
            setValidationMessages([]);
            setError("");

            await Promise.all([refreshLoanApprovalPageData(), loadLoanApprovalPersonaPermission(), checkIfLoanLocked()]);
        } catch (err) {
            console.error("LoanApprovalPage: handlePageRefresh failed", err);

            showToast(err?.message || "Failed to refresh the Loan Approval page.", "error");
        } finally {
            setPageRefreshing(false);
        }
    }

    async function syncHostLoanWorkspace() {
        if (isLocalEncompassMode()) {
            return;
        }

        try {
            const loanObject = await window.elli.script.getObject("loan");

            await loanObject.merge();
            await loanObject.calculate();
        } catch (err) {
            console.warn("LoanApprovalPage: merge/calculate failed:", err);
        }
    }

    async function ensureLockValidationCondition(token, currentLoanId, latestConditions) {
        const existingCondition = getLockValidationCondition(latestConditions);

        if (!existingCondition) {
            try {
                const serviceAccessToken = await getLoanConditionServiceAccessToken();

                await addLoanCondition(serviceAccessToken, currentLoanId, {
                    title: CONDITION_TITLE_LOCK_VALIDATION,
                    conditionType: "Underwriting",
                    priorTo: "Docs"
                });

                return true;
            } catch (conditionError) {
                const errorCode = logError("LOAN_APPROVAL_CREATE_CONDITION_ERROR");

                logAppInsightError("Loan Approval Condition Creation Error", {
                    component: "LoanApprovalPage",
                    problemId: errorCode,
                    errorCode,
                    rawError: conditionError,
                    errorMessage: `${errorCode} - Failed to create the Lock Validation Required condition.`
                });
                console.error("LoanApprovalPage: failed to create the Lock Validation Required condition", conditionError);

                try {
                    await postLoanApprovalFailureEvent({
                        eventType: LOAN_APPROVAL_FAILURE_EVENT_TYPES.CREATE_CONDITION_FAILED,
                        operation: "Create Lock Validation Required condition",
                        error: conditionError
                    });
                } catch (eventError) {
                    console.error("LoanApprovalPage: failed to report the condition creation error", eventError);
                }

                return false;
            }
        }

        if (isConditionClearedByTracking(existingCondition)) {
            try {
                await addConditionTracking(token, currentLoanId, existingCondition.id, {
                    status: "Cleared",
                    isChecked: false
                });
            } catch (conditionTrackingError) {
                const errorCode = logError("LOAN_APPROVAL_CLEAR_CONDITION_ERROR");

                logAppInsightError("Loan Approval Condition Clear Error", {
                    component: "LoanApprovalPage",
                    problemId: errorCode,
                    errorCode,
                    rawError: conditionTrackingError,
                    errorMessage: `${errorCode} - Failed to clear the Lock Validation Required condition status.`
                });

                console.error("LoanApprovalPage: failed to clear the Lock Validation Required condition status", conditionTrackingError);

                try {
                    await postLoanApprovalFailureEvent({
                        eventType: LOAN_APPROVAL_FAILURE_EVENT_TYPES.CLEAR_CONDITION_STATUS_FAILED,
                        operation: "Clear Lock Validation Required condition status",
                        error: conditionTrackingError
                    });
                } catch (eventError) {
                    console.error("LoanApprovalPage: failed to report the condition status clear error", eventError);
                }

                return false;
            }
        }

        return true;
    }

    async function completeMilestoneByName(token, currentLoanId, currentMilestones, milestoneName) {
        const milestone = findMilestoneByName(currentMilestones, milestoneName);

        if (!milestone) {
            throw new Error(`Milestone "${milestoneName}" was not found.`);
        }

        if (milestone.doneIndicator === true) {
            await updateLoanMilestone(token, currentLoanId, milestone.id, {
                doneIndicator: false
            });

            const refreshedMilestones = await fetchLoanMilestones(token, currentLoanId);

            const refreshedMilestone = findMilestoneByName(refreshedMilestones, milestoneName);

            if (!refreshedMilestone) {
                throw new Error(`Milestone "${milestoneName}" was not found after reopening.`);
            }

            await updateLoanMilestone(token, currentLoanId, refreshedMilestone.id, {
                doneIndicator: true
            });

            return;
        }

        await updateLoanMilestone(token, currentLoanId, milestone.id, {
            doneIndicator: true
        });
    }

    async function reopenMilestoneByName(token, currentLoanId, currentMilestones, milestoneName) {
        const milestone = findMilestoneByName(currentMilestones, milestoneName);

        if (!milestone) {
            throw new Error(`Milestone "${milestoneName}" was not found.`);
        }

        if (milestone.doneIndicator === false) {
            return;
        }

        await updateLoanMilestone(token, currentLoanId, milestone.id, {
            doneIndicator: false
        });
    }

    async function callLoanApprovalApproveApi() {
        const authCode = await getEncompassAuthCode();

        const endpoint = `${API_DOMAIN}/api/LoanApproval/approve`;

        const response = await fetch(endpoint, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                loanId,
                authCode,
                docType: decision.docType,
                notificationType: decision.notificationType,
                notifyBroker: decision.notifyBroker,
                notifyBrokerWithAttachment: decision.notifyBrokerWithAttachment
            })
        });

        if (!response.ok) {
            const text = await response.text().catch(() => response.statusText || "Unknown error");

            throw new Error(`Loan Approval approve API failed: ${response.status} - ${text}`);
        }
    }

    async function previewGeneratedPdf() {
        if (!decision?.docType || decision?.disablePreview) {
            showToast("Preview is not available for this workflow.", "error");

            return;
        }

        let blobUrl = "";

        try {
            setPreviewLoading(true);
            setPreviewVisible(true);
            setPdfPages([]);

            setPreviewTitle(decision.docType === "conditional" ? "Conditional Approval Preview" : "Final Approval Preview");

            const authCode = await getEncompassAuthCode();

            const endpoint = `${API_DOMAIN}/api/LoanApproval/preview-pdf-only`;

            const response = await fetch(endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    loanId,
                    authCode,
                    docType: decision.docType
                })
            });

            if (!response.ok) {
                const text = await response.text().catch(() => response.statusText || "Unknown error");

                throw new Error(text);
            }

            const blob = await response.blob();

            blobUrl = URL.createObjectURL(blob);

            const pages = await renderPDF(blobUrl);

            setPdfPages(pages);
        } catch (err) {
            console.error("LoanApprovalPage: previewGeneratedPdf error:", err);

            showToast(`Unable to preview the document. ${err?.message || ""}`, "error");
        } finally {
            if (blobUrl) {
                URL.revokeObjectURL(blobUrl);
            }

            setPreviewLoading(false);
        }
    }

    async function postLoanApprovalEvent({ action, targetMilestone }) {
        const authObject = await getEncompassAuthObject();
        const accessTokenResponse = await authObject.getAccessToken();
        const accessToken = accessTokenResponse?.access_token;

        if (!accessToken) {
            throw new Error("Encompass access token could not be obtained.");
        }

        const loanApprovalRules = currentMilestonePermission?.loanApprovalRules || [];

        const matchingLoanApprovalRule = findMatchingLoanApprovalRule({
            loanApprovalRules,
            workflowType: milestonePersonaAccess.workflowType,
            lockValidation: lockValidationDisplay,
            openPTDCondition: openPTDDisplay
        });

        const eventLoanApprovalRule = matchingLoanApprovalRule
            ? {
                ...matchingLoanApprovalRule,
                ...(matchingLoanApprovalRule.openPTDCondition ? { openPTDConditionCount: openPTDCount } : {})
            }
            : null;

        const eventPayload = {
            eventId: window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            eventTime: new Date().toISOString(),
            eventType: "loanapproval",
            meta: {
                userId: user?.id || "",
                resourceType: "Loan",
                resourceId: loanId,
                instanceId: user?.instanceId || "",
                resourceRef: `/encompass/v3/loans/${loanId}/milestone`,
                payload: {
                    event: {
                        milestoneChanges: [
                            {
                                action,
                                currentMilestone: normalizedCurrentMilestone,
                                targetMilestone: targetMilestone || ""
                            }
                        ],
                        loanApprovalRules: eventLoanApprovalRule ? [eventLoanApprovalRule] : []
                    }
                }
            }
        };

        const response = await fetch(`${API_DOMAIN}/api/LoanApproval/event`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify(eventPayload)
        });

        if (!response.ok) {
            const message = await response.text();

            throw new Error(`Loan Approval event API failed: ${response.status} - ${message}`);
        }
    }

    async function postLoanApprovalFailureEvent({ eventType, operation, error }) {
        const authObject = await getEncompassAuthObject();
        const accessTokenResponse = await authObject.getAccessToken();
        const accessToken = accessTokenResponse?.access_token;

        if (!accessToken) {
            throw new Error("Encompass access token could not be obtained.");
        }

        let errorDetails = "";

        if (typeof error?.response?.data === "string") {
            errorDetails = error.response.data;
        } else if (error?.response?.data) {
            try {
                errorDetails = JSON.stringify(error.response.data);
            } catch {
                errorDetails = String(error.response.data);
            }
        }

        const eventPayload = {
            eventId: window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            eventTime: new Date().toISOString(),
            eventType,
            meta: {
                userId: user?.id || "",
                resourceType: "Loan",
                resourceId: loanId,
                instanceId: user?.instanceId || "",
                resourceRef: `/encompass/v3/loans/${loanId}/milestone`,
                payload: {
                    event: {
                        milestoneChanges: [],
                        loanApprovalRules: [],
                        error: {
                            operation,
                            message: error?.message || "Unknown Loan Approval error.",
                            statusCode: error?.response?.status || null,
                            details: errorDetails
                        }
                    }
                }
            }
        };

        const response = await fetch(`${API_DOMAIN}/api/LoanApproval/event`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify(eventPayload)
        });

        if (!response.ok) {
            const message = await response.text();

            throw new Error(`Loan Approval failure event API failed: ${response.status} - ${message}`);
        }
    }

    async function runMilestoneRuleExecutionMiddleware({ milestoneName, pendingAction, checkRuleGate = false, blockedToastMessage = "" }) {
        if (checkRuleGate) {
            return true;
        }

        setProgress(30);
        setLoadingText("Running milestone rule execution...");

        const ruleGateResult = await runPreMilestoneCompletionRules({
            loanId,
            milestoneName,
            requestedBy: user?.id || user?.userId || `${user?.firstName || ""} ${user?.lastName || ""}`.trim()
        });

        if (!ruleGateResult.executed) {
            return true;
        }

        setRulesModalResult(ruleGateResult.rulesResult);
        setRulesModalOpen(true);

        if (ruleGateResult.hasBlockingIssues) {
            setPendingRuleGateAction(null);

            if (blockedToastMessage) {
                showToast(blockedToastMessage, "error");
            }
        } else {
            setPendingRuleGateAction(pendingAction);
        }

        return false;
    }

    async function handleUnderwriterApprove(confirmationAccepted = false, checkRuleGate = false) {
        setValidationMessages([]);
        if (!loanId) {
            showToast("Loan ID was not found.", "error");

            return;
        }

        if (
            !workflowPermissionLoaded ||
            !milestonePersonaAccess.hasAccess ||
            ![WORKFLOW_TYPES.UNDERWRITER, WORKFLOW_TYPES.COLLATERAL_UNDERWRITER, WORKFLOW_TYPES.UW_MANAGER].includes(milestonePersonaAccess.workflowType)
        ) {
            showToast(
                personaPermissionStatus === "failed" ? "Workflow permissions could not be loaded. Approval is disabled." : "You do not have permission to approve this milestone.",
                "error"
            );

            return;
        }

        if (!decision?.isSupported || decision?.disableApprove) {
            showToast(decision?.message || "No underwriting workflow is available.", "error");

            return;
        }

        if (loanLocked) {
            showToast(`This loan is locked by '${lockedByUser}'. You cannot approve it.`, "error");

            return;
        }

        if (isUwManagerReturnWorkflow && !confirmationAccepted) {
            setApproveConfirmationVisible(true);

            return;
        }

        setApproveConfirmationVisible(false);

        try {
            setProcessing(true);
            setProgress(10);

            setLoadingText("Starting the loan approval workflow...");

            const authCode = await getEncompassAuthCode();

            const token = await getAccessToken(authCode);

            if (decision.milestoneAction === "complete" && !checkRuleGate) {
                setProgress(25);
                setLoadingText("Running milestone rule execution...");

                const shouldContinueWorkflow = await runMilestoneRuleExecutionMiddleware({
                    milestoneName: decision?.targetMilestone || currentMilestone,
                    pendingAction: {
                        type: "underwriter",
                        confirmationAccepted
                    },
                    checkRuleGate
                });

                if (!shouldContinueWorkflow) {
                    return;
                }
            }

            setProgress(20);

            setLoadingText("Refreshing milestone data...");

            /*
             * Milestone update must happen before any
             * condition, document, or email operations.
             */
            const latestMilestones = await fetchLoanMilestones(token, loanId);

            if (decision.targetMilestone) {
                setProgress(35);

                setLoadingText(`Updating ${decision.targetMilestone}...`);

                if (decision.milestoneAction === "complete") {
                    await completeMilestoneByName(token, loanId, latestMilestones, decision.targetMilestone);
                }

                if (decision.milestoneAction === "reopen") {
                    await reopenMilestoneByName(token, loanId, latestMilestones, decision.targetMilestone);
                }
            }

            /*
             * Nothing below this point runs when the
             * milestone update throws an error.
             */
            if (decision.ensureLockValidationCondition) {
                setProgress(50);

                setLoadingText('Creating or reopening the "Lock Validation Required" condition...');

                let latestConditions = await fetchLoanConditionsWithServiceToken(loanId);

                const conditionUpdated = await ensureLockValidationCondition(token, loanId, latestConditions);

                if (conditionUpdated) {
                    latestConditions = await fetchLoanConditionsWithServiceToken(loanId);

                    setConditions(latestConditions);
                } else {
                    setConditions(latestConditions);
                }
            }

            if (decision.docType) {
                setProgress(70);

                const approvalType = decision.docType === "conditional" ? "Conditional Approval" : "Final Approval";
                const brokerWillBeNotified = decision.notifyBroker || decision.notifyBrokerWithAttachment;

                setLoadingText(
                    brokerWillBeNotified ? `Generating the ${approvalType} letter and notifying the broker...` : `Generating the ${approvalType} letter...`
                );

                try {
                    await callLoanApprovalApproveApi();
                } catch (approveApiError) {
                    const errorCode = logError("LOAN_APPROVAL_APPROVE_API_ERROR");

                    logAppInsightError("Loan Approval Approve API Error", {
                        component: "LoanApprovalPage",
                        problemId: errorCode,
                        errorCode,
                        rawError: approveApiError,
                        errorMessage: `${errorCode} - Failed to call the Loan Approval approve API.`
                    });

                    console.error("LoanApprovalPage: callLoanApprovalApproveApi failed", approveApiError);

                    try {
                        await postLoanApprovalFailureEvent({
                            eventType: LOAN_APPROVAL_FAILURE_EVENT_TYPES.APPROVE_API_FAILED,
                            operation: "Call Loan Approval approve API",
                            error: approveApiError
                        });
                    } catch (eventError) {
                        console.error("LoanApprovalPage: failed to report the Loan Approval approve API error", eventError);
                    }
                }
            }

            if (decision.generateSupportingDocuments) {
                setProgress(85);

                setLoadingText(getSupportingDocumentLoadingText());

                await generateAdditionalDocuments(loanId);
            }

            setProgress(95);

            setLoadingText("Refreshing the loan workspace...");

            await syncHostLoanWorkspace();
            await refreshLoanApprovalPageData();

            try {
                await postLoanApprovalEvent({
                    action: isUwManagerReturnWorkflow ? "Submit Final Review" : "Approve",
                    targetMilestone: decision.targetMilestone
                });
            } catch (eventError) {
                console.error("LoanApprovalPage: failed to save approval event", eventError);

                //showToast("Loan approval completed, but the approval event could not be saved.", "warning");
            }

            setProgress(100);
            setLoadingText("Completed.");

            if (isUwManagerReturnWorkflow) {
                showToast("Loan returned to Submit for Final Review.", "success");
            } else if (decision.milestoneAction === "reopen" && decision.targetMilestone === MILESTONE_CONDITIONAL_APPROVAL) {
                showToast(
                    "Readiness checks are still pending. Conditional Approval was reopened, a new Conditional Approval letter was generated, and the broker was notified.",
                    "success"
                );
            } else {
                showToast(`Loan approved and submitted to ${decision.targetMilestone}.`, "success");
            }
        } catch (err) {
            const errorCode = logError("LOAN_APPROVAL_APPROVE_ERROR");

            logAppInsightError("Loan Approval Approve Error", {
                component: "LoanApprovalPage",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed during Loan Approval processing.`
            });

            console.error("LoanApprovalPage: handleUnderwriterApprove failed", err);

            const milestoneValidationMessages = Array.isArray(err?.validationMessages) ? err.validationMessages : [];

            if (err?.code === "MILESTONE_VALIDATION" && milestoneValidationMessages.length > 0) {
                setValidationMessages(milestoneValidationMessages);
            } else {
                showToast(err?.message || "Failed to process Loan Approval.", "error");
            }
        } finally {
            setProcessing(false);
            setLoadingText("");
        }
    }

    async function handleAccountManagerRoute(targetMilestone, checkRuleGate = false) {
        setValidationMessages([]);
        if (!loanId) {
            showToast("Loan ID was not found.", "error");

            return;
        }

        if (!workflowPermissionLoaded || !milestonePersonaAccess.hasAccess || milestonePersonaAccess.workflowType !== WORKFLOW_TYPES.ACCOUNT_MANAGER) {
            showToast(
                personaPermissionStatus === "failed"
                    ? "Workflow permissions could not be loaded. Milestone routing is disabled."
                    : "You do not have permission to route this milestone.",
                "error"
            );

            return;
        }

        if (!targetMilestone) {
            showToast("The target milestone is not configured in the matching loan approval rule.", "error");

            return;
        }

        if (loanLocked) {
            showToast(`This loan is locked by '${lockedByUser}'. You cannot update the milestone.`, "error");

            return;
        }

        try {
            setProcessing(true);
            setProgress(15);

            setLoadingText(`Preparing to submit for ${targetMilestone}...`);

            const authCode = await getEncompassAuthCode();

            const token = await getAccessToken(authCode);

            setProgress(30);

            const shouldContinueWorkflow = await runMilestoneRuleExecutionMiddleware({
                milestoneName: targetMilestone || currentMilestone,
                pendingAction: {
                    type: "accountManager",
                    targetMilestone
                },
                checkRuleGate,
                blockedToastMessage: "Milestone completion blocked by rule execution."
            });

            if (!shouldContinueWorkflow) {
                return;
            }

            setProgress(45);

            setLoadingText("Refreshing milestone data...");

            const latestMilestones = await fetchLoanMilestones(token, loanId);

            setProgress(75);

            setLoadingText(`Submitting the loan to ${targetMilestone}...`);

            await completeMilestoneByName(token, loanId, latestMilestones, targetMilestone);

            setProgress(95);

            setLoadingText("Refreshing the loan workspace...");

            await syncHostLoanWorkspace();
            await refreshLoanApprovalPageData();

            try {
                await postLoanApprovalEvent({
                    action: targetMilestone === MILESTONE_SUBMIT_FOR_CONDITION_REVIEW ? "Submit Condition Review" : "Submit Final Review",
                    targetMilestone
                });
            } catch (eventError) {
                console.error("LoanApprovalPage: failed to save routing event", eventError);

                //showToast("Loan submission completed, but the approval event could not be saved.", "warning");
            }

            setProgress(100);
            setLoadingText("Completed.");

            showToast(`Loan submitted to ${targetMilestone}.`, "success");
        } catch (err) {
            const errorCode = logError("LOAN_APPROVAL_AM_ROUTE_ERROR");

            logAppInsightError("Loan Approval Account Manager Route Error", {
                component: "LoanApprovalPage",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed to route Account Manager milestone.`
            });

            console.error("LoanApprovalPage: handleAccountManagerRoute failed", err);

            const milestoneValidationMessages = Array.isArray(err?.validationMessages) ? err.validationMessages : [];

            if (err?.code === "MILESTONE_VALIDATION" && milestoneValidationMessages.length > 0) {
                setValidationMessages(milestoneValidationMessages);
            } else {
                showToast(err?.message || "Failed to route the loan.", "error");
            }
        } finally {
            setProcessing(false);
            setLoadingText("");
        }
    }

    function getSupportingDocumentLoadingText() {
        if ((loanType || "").toUpperCase() === "CONSUMER") {
            return "Generating 1008 and 2020 URLA supporting documents...";
        }

        return "Generating 1008 and Commercial Loan Application supporting documents...";
    }

    async function checkIfLoanLocked() {
        try {
            const authCode = await getEncompassAuthCode();

            const token = await getAccessToken(authCode);

            const result = await checkLoanResourceLock(token, loanId);

            if (result?.locked) {
                setLoanLocked(true);

                setLockedByUser(result.userId || result.fullName || "another user");

                return;
            }

            setLoanLocked(false);
            setLockedByUser("");
        } catch (err) {
            console.error("LoanApprovalPage: checkIfLoanLocked error:", err);
        }
    }

    async function renderPDF(blobUrl) {
        const loadingTask = pdfjsLib.getDocument({
            url: blobUrl,
            disableWorker: true
        });

        const pdf = await loadingTask.promise;

        const pageList = [];

        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
            const page = await pdf.getPage(pageNumber);

            const viewport = page.getViewport({
                scale: 1.2
            });

            const canvas = document.createElement("canvas");

            const context = canvas.getContext("2d");

            canvas.width = viewport.width;

            canvas.height = viewport.height;

            await page.render({
                canvasContext: context,
                viewport
            }).promise;

            pageList.push(canvas.toDataURL());
        }

        return pageList;
    }

    async function previewExistingCertificate(attachmentId, type) {
        try {
            setPreviewLoading(true);
            setPreviewVisible(true);
            setPdfPages([]);
            setPreviewTitle(type === "conditional" ? "Conditional Approval Certificate" : "Final Approval Certificate");

            const authCode = await getEncompassAuthCode();

            const blob = await downloadApprovalCertificateFromApi(authCode, loanId, attachmentId, type);

            const blobUrl = URL.createObjectURL(blob);
            const pages = await renderPDF(blobUrl);
            setPdfPages(pages);
        } catch (err) {
            console.error("LoanApprovalPage: previewExistingCertificate error:", err);
            showToast("Unable to preview document.", "error");
        } finally {
            setPreviewLoading(false);
        }
    }

    const downloadExistingCertificate = useCallback(
        async record => {
            try {
                const authCode = await getEncompassAuthCode();

                const blob = await downloadApprovalCertificateFromApi(authCode, loanId, record.attachmentId, record.attachmentType);

                const url = URL.createObjectURL(blob);

                const link = document.createElement("a");

                link.href = url;
                link.download = `${sanitizeFileName(record.title)}.pdf`;

                document.body.appendChild(link);
                link.click();
                link.remove();

                URL.revokeObjectURL(url);
            } catch (err) {
                console.error("LoanApprovalPage: downloadExistingCertificate error:", err);

                showToast("Failed to download the approval letter.", "error");
            }
        },
        [loanId, showToast]
    );

    const approvalGridRows = useMemo(
        () =>
            sortedApprovalAttachments.map((attachment, index) => ({
                key: attachment.id || `${attachment.title || "approval"}-${index}`,

                attachmentId: attachment.id,

                attachmentType: getApprovalAttachmentType(attachment),

                document:
                    attachment.document ||
                    attachment.assignedToEntityName ||
                    attachment.assignedToEntityType ||
                    attachment.assignedTo?.entityName ||
                    attachment.assignedTo?.entityType ||
                    "-",

                title: attachment.title || "Approval Letter",

                version: attachment.currentVersion ?? attachment.isActive ?? null,

                createdDate: getCreatedDateDisplay(attachment)
            })),
        [sortedApprovalAttachments]
    );

    useEffect(() => {
        const maximumPage = Math.max(1, Math.ceil(approvalGridRows.length / approvalPageSize));

        if (approvalPage > maximumPage) {
            setApprovalPage(maximumPage);
        }
    }, [approvalGridRows.length, approvalPage, approvalPageSize]);

    const pagedApprovalGridRows = useMemo(() => {
        const startIndex = (approvalPage - 1) * approvalPageSize;

        return approvalGridRows.slice(startIndex, startIndex + approvalPageSize);
    }, [approvalGridRows, approvalPage, approvalPageSize]);

    const approvalPageStart = approvalGridRows.length === 0 ? 0 : (approvalPage - 1) * approvalPageSize + 1;

    const approvalPageEnd = Math.min(approvalPage * approvalPageSize, approvalGridRows.length);

    const approvalGridColumns = useMemo(
        () => [
            {
                title: "Document",
                dataIndex: "document",
                key: "document",
                width: "32%",
                ellipsis: true,
                render: value => (
                    <Tooltip title={value || ""}>
                        <div className="flex min-w-0 items-center gap-3">
                            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-slate-50 text-slate-500">
                                <FileText size={14} />
                            </span>

                            <span className="truncate text-[13px] font-medium text-slate-800">{value || "-"}</span>
                        </div>
                    </Tooltip>
                )
            },
            {
                title: "Title",
                dataIndex: "title",
                key: "title",
                width: "28%",
                ellipsis: true,
                render: value => (
                    <Tooltip title={value || ""}>
                        <span className="block truncate text-[13px] font-semibold text-slate-900">{value || "-"}</span>
                    </Tooltip>
                )
            },
            {
                title: "Version",
                dataIndex: "version",
                key: "version",
                width: "14%",
                render: value => <VersionBadge isCurrent={getCurrentVersionState(value) === true} />
            },
            {
                title: "Created (Pacific)",
                dataIndex: "createdDate",
                key: "createdDate",
                width: "17%",
                render: value => <span className="whitespace-nowrap text-[12px] font-medium text-slate-600">{value || "-"}</span>
            },
            {
                title: "Actions",
                key: "actions",
                width: "9%",
                render: (_, record) => (
                    <div className="flex items-center gap-4">
                        <Tooltip title="Preview PDF">
                            <button className="hover:opacity-80" onClick={() => previewExistingCertificate(record.attachmentId, record.attachmentType)}>
                                <Eye size={18} />
                            </button>
                        </Tooltip>
                        <Tooltip title="Download PDF">
                            <button
                                type="button"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-600 shadow-sm transition hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700"
                                onClick={() => downloadExistingCertificate(record)}
                            >
                                <Download size={15} />
                            </button>
                        </Tooltip>
                    </div>
                )
            }
        ],
        [downloadExistingCertificate]
    );

    const underwriterMessage = useMemo(() => {
        const current = normalizedCurrentMilestone || "the current milestone";

        const target = decision?.targetMilestone || "the next milestone";

        const personaName = milestonePersonaAccess.matchedPersona || "an authorized persona";

        if (isUwManagerReturnWorkflow) {
            return `You're signed in with the ${personaName} persona. This loan is at ${current}. Use Submit for final review to return the loan to ${target}.`;
        }

        return `You're signed in with the ${personaName} persona. This loan is at ${current}. Review the readiness checks below, then approve to advance the loan to ${target}.`;
    }, [decision, isUwManagerReturnWorkflow, normalizedCurrentMilestone, milestonePersonaAccess.matchedPersona]);

    const continueAfterRulesValidation = async () => {
        const pendingAction = pendingRuleGateAction;



        setRulesModalOpen(false);
        setPendingRuleGateAction(null);

        if (!pendingAction) {
            return;
        }

        if (pendingAction.type === "underwriter") {
            await handleUnderwriterApprove(Boolean(pendingAction.confirmationAccepted), true);
            return;
        }

        if (pendingAction.type === "accountManager") {
            await handleAccountManagerRoute(pendingAction.targetMilestone, true);
        }
    };

    return (
        <>
            <LoadingOverlay loading={loading} text="Loading..." />

            <div className="min-h-screen px-5 py-5">
                <div className="mx-auto w-full max-w-[1400px] space-y-5">
                    {validationMessages.length > 0 && (
                        <section className="w-full overflow-hidden rounded-lg border border-red-300 bg-red-50 shadow-sm">
                            <div className="flex items-start justify-between gap-4 px-5 py-4">
                                <div className="flex min-w-0 items-start gap-3">
                                    <AlertTriangle size={21} className="mt-0.5 shrink-0 text-red-600" />

                                    <div className="min-w-0">
                                        <div className="text-[14px] font-semibold text-red-800">Validation Message(s)</div>

                                        {(() => {
                                            const { plainMessages, groups } = groupValidationMessages(validationMessages);

                                            return (
                                                <ul className="mt-2 space-y-2 pl-5 text-[13px] leading-5 text-red-700">
                                                    {plainMessages.map((message, index) => (
                                                        <li key={`plain-${index}`} className="list-disc break-words">
                                                            {message}
                                                        </li>
                                                    ))}

                                                    {groups.map((group, groupIndex) => (
                                                        <li key={`group-${groupIndex}`} className="list-disc break-words">
                                                            <span className="font-semibold text-red-800">{group.milestoneName}</span>

                                                            <ul className="mt-1 space-y-1 pl-5">
                                                                {group.messages.map((message, msgIndex) => (
                                                                    <li key={`group-${groupIndex}-msg-${msgIndex}`} className="list-[circle] break-words">
                                                                        {message}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </li>
                                                    ))}
                                                </ul>
                                            );
                                        })()}
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-red-500 transition hover:bg-red-100 hover:text-red-700"
                                    onClick={() => setValidationMessages([])}
                                    aria-label="Close validation messages"
                                >
                                    <X size={17} />
                                </button>
                            </div>
                        </section>
                    )}

                    {error ? (
                        <div className="w-full rounded-lg border border-red-200 bg-red-50 px-5 py-4 text-[15px] text-red-700">{error}</div>
                    ) : (

                        <>
                            {showWorkflow ? (
                                <section className="w-full rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                                    <div className="flex items-start gap-3">
                                        <div className="min-w-0 flex-1">
                                            <MilestoneProgressBar
                                                currentMilestone={progressCurrentMilestone}
                                                targetMilestone={progressTargetMilestone}
                                                milestoneOrder={progressMilestoneOrder}
                                            />
                                        </div>

                                        <Tooltip title="Refresh Loan Approval Workflow">
                                            <button
                                                type="button"
                                                className="mt-3 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-600 shadow-sm transition hover:border-blue-400 hover:bg-blue-50 hover:text-[#2080C3] disabled:cursor-not-allowed disabled:opacity-50"
                                                onClick={handlePageRefresh}
                                                disabled={pageRefreshing || processing || !loanId}
                                                aria-label="Refresh Loan Approval Workflow"
                                            >
                                                <RefreshCw size={17} className={pageRefreshing ? "animate-spin" : ""} />
                                            </button>
                                        </Tooltip>
                                    </div>

                                    {loanLocked && (
                                        <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 px-5 py-4 text-[13px] text-amber-800">
                                            This loan is locked by '{lockedByUser}'. Workflow actions are disabled.
                                        </div>
                                    )}

                                    {showUnderwriterWorkflow && (
                                        <UnderwriterWorkflow
                                            message={underwriterMessage}
                                            decision={decision}
                                            lockValidationDisplay={lockValidationDisplay}
                                            openPTDDisplay={openPTDDisplay}
                                            openPTDCount={openPTDCount}
                                            hasOpenPTD={hasOpenPTD}
                                            processing={processing}
                                            progress={progress}
                                            loadingText={loadingText}
                                            loanLocked={loanLocked}
                                            onPreview={previewGeneratedPdf}
                                            onApprove={() => handleUnderwriterApprove(false)}
                                        />
                                    )}

                                    {showAccountManagerWorkflow && (
                                        <AccountManagerWorkflow
                                            personaName={milestonePersonaAccess.matchedPersona}
                                            currentMilestone={normalizedCurrentMilestone}
                                            decision={decision}
                                            lockValidationDisplay={lockValidationDisplay}
                                            processing={processing}
                                            progress={progress}
                                            loadingText={loadingText}
                                            loanLocked={loanLocked}
                                            conditionReviewApproved={conditionalApprovalCompleted}
                                            onSubmitConditionReview={() => handleAccountManagerRoute(MILESTONE_SUBMIT_FOR_CONDITION_REVIEW)}
                                            onSubmitFinalReview={() => handleAccountManagerRoute(MILESTONE_SUBMIT_FOR_FINAL_REVIEW)}
                                            onUpgradeToFinal={() => handleAccountManagerRoute(decision?.targetMilestone)}
                                        />
                                    )}
                                </section>
                            ) : personaPermissionStatus === "failed" ? (
                                <section className="flex w-full items-center justify-between gap-4 rounded-lg border border-red-200 bg-red-50 px-5 py-4 text-[15px] text-red-800">
                                    <div className="min-w-0">
                                        {personaPermissionError || "Workflow permissions could not be loaded. Workflow actions are disabled."} Recent loan approvals are shown below.
                                    </div>

                                    <Tooltip title="Refresh Loan Approval Workflow">
                                        <button
                                            type="button"
                                            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-600 shadow-sm transition hover:border-blue-400 hover:bg-blue-50 hover:text-[#2080C3] disabled:cursor-not-allowed disabled:opacity-50"
                                            onClick={handlePageRefresh}
                                            disabled={pageRefreshing || processing || !loanId}
                                            aria-label="Refresh Loan Approval Workflow"
                                        >
                                            <RefreshCw size={17} className={pageRefreshing ? "animate-spin" : ""} />
                                        </button>
                                    </Tooltip>
                                </section>
                            ) : (
                                <section className="flex w-full items-center justify-between gap-4 rounded-lg border border-blue-200 bg-blue-50 px-5 py-4 text-[15px] text-blue-800">
                                    <div className="min-w-0">
                                        No workflow actions are available for your role at this milestone. Recent loan approvals are shown below.
                                    </div>

                                    <Tooltip title="Refresh Loan Approval Workflow">
                                        <button
                                            type="button"
                                            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-600 shadow-sm transition hover:border-blue-400 hover:bg-blue-50 hover:text-[#2080C3] disabled:cursor-not-allowed disabled:opacity-50"
                                            onClick={handlePageRefresh}
                                            disabled={pageRefreshing || processing || !loanId}
                                            aria-label="Refresh Loan Approval Workflow"
                                        >
                                            <RefreshCw size={17} className={pageRefreshing ? "animate-spin" : ""} />
                                        </button>
                                    </Tooltip>
                                </section>
                            )}

                            <section className="w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                                <div className="flex h-11 items-center justify-between border-b border-slate-200 px-4">
                                    <div className="flex items-center gap-3">
                                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-blue-50 text-[#2080C3]">
                                            <FileText size={14} />
                                        </span>

                                        <div className="text-[14px] font-semibold text-slate-900">Approval Certificate</div>

                                        <span className="inline-flex min-w-6 items-center justify-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                                            {approvalGridRows.length}
                                        </span>
                                    </div>
                                </div>

                                {attachmentsLoading ? (
                                    <div className="flex h-40 items-center justify-center">
                                        <Spin size="large" />
                                    </div>
                                ) : (
                                    <Table
                                        className={["loan-approval-table", approvalGridRows.length === 0 ? "loan-approval-table-empty" : ""].join(" ")}
                                        size="small"
                                        columns={approvalGridColumns}
                                        dataSource={pagedApprovalGridRows}
                                        rowKey="key"
                                        tableLayout="fixed"
                                        scroll={
                                            approvalGridRows.length > 0
                                                ? {
                                                    x: 1080
                                                }
                                                : undefined
                                        }
                                        pagination={false}
                                        locale={{
                                            emptyText: (
                                                <div className="flex h-[72px] items-center justify-center text-[12px] text-slate-400">
                                                    No approval certificates were found for this loan.
                                                </div>
                                            )
                                        }}
                                    />
                                )}

                                <div className="flex min-h-[46px] flex-col gap-2 border-t border-slate-200 bg-slate-50/70 px-4 py-2 sm:flex-row sm:items-center sm:justify-between">
                                    <button
                                        type="button"
                                        className="inline-flex h-8 items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-[11px] font-semibold text-slate-700 shadow-sm transition hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                                        onClick={fetchApprovalCertificateGrid}
                                        disabled={attachmentsLoading || !loanId}
                                    >
                                        <RefreshCw size={15} className={attachmentsLoading ? "animate-spin" : ""} />
                                        Refresh
                                    </button>

                                    {approvalGridRows.length > 0 && (
                                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                                            <span className="whitespace-nowrap text-[12px] font-medium text-slate-500">
                                                {approvalPageStart}–{approvalPageEnd} of {approvalGridRows.length}
                                            </span>

                                            <Pagination
                                                className="loan-approval-pagination"
                                                size="small"
                                                current={approvalPage}
                                                pageSize={approvalPageSize}
                                                total={approvalGridRows.length}
                                                showSizeChanger
                                                showLessItems
                                                pageSizeOptions={["10", "20", "50"]}
                                                onChange={(nextPage, nextPageSize) => {
                                                    if (nextPageSize !== approvalPageSize) {
                                                        setApprovalPageSize(nextPageSize);

                                                        setApprovalPage(1);
                                                        return;
                                                    }

                                                    setApprovalPage(nextPage);
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </section>
                        </>

                    )}

                </div>
            </div>

            <Toast visible={toastVisible} type={toastType} message={toastMessage} onClose={() => setToastVisible(false)} />

            <RulesValidationModalV2
                isOpen={rulesModalOpen}
                result={rulesModalResult}
                onClose={() => {
                    setRulesModalOpen(false);
                    setPendingRuleGateAction(null);
                }}
                onContinueSave={continueAfterRulesValidation}
            />

            <Modal
                open={approveConfirmationVisible}
                title="Confirm Submission"
                okText="Submit for final review"
                cancelText="Cancel"
                centered
                getContainer={false}
                confirmLoading={processing}
                onOk={() => handleUnderwriterApprove(true)}
                onCancel={() => setApproveConfirmationVisible(false)}
            >
                <p>Are you sure you want to move this loan to &quot;Submit for Final Review&quot;?</p>
            </Modal>

            <Modal
                open={previewVisible}
                onCancel={() => setPreviewVisible(false)}
                footer={null}
                width="90vw"
                centered
                title={previewTitle}
                styles={{
                    body: {
                        padding: 0,
                        backgroundColor: "#f5f7fa",
                        height: "82vh",
                        overflowY: "auto"
                    },
                    header: {
                        paddingBottom: 12,
                        borderBottom: "1px solid #e5e7eb"
                    },
                    content: {
                        borderRadius: 10,
                        overflow: "hidden"
                    },
                    mask: {
                        backgroundColor: "rgba(0,0,0,0.45)"
                    }
                }}
            >
                {previewLoading ? (
                    <div className="flex h-full items-center justify-center">
                        <Spin size="large" />
                    </div>
                ) : (
                    <div className="flex flex-col items-center bg-gray-100 p-4">
                        {pdfPages.map((source, index) => (
                            <img key={`page-${index}`} src={source} alt={`Page ${index + 1}`} className="mb-4 h-auto w-full max-w-[1000px] shadow" />
                        ))}
                    </div>
                )}
            </Modal>
        </>
    );
}

function UnderwriterWorkflow({
    message,
    decision,
    lockValidationDisplay,
    openPTDDisplay,
    openPTDCount,
    hasOpenPTD,
    processing,
    progress,
    loadingText,
    loanLocked,
    onPreview,
    onApprove
}) {
    const isSubmitForFinalReviewOnly =
        decision?.milestoneAction === "reopen" &&
        decision?.targetMilestone === MILESTONE_SUBMIT_FOR_FINAL_REVIEW &&
        !decision?.docType &&
        !decision?.generateSupportingDocuments;

    return (
        <div className="mt-2 space-y-3">
            <PersonaMessage icon={<UserRound size={18} />} message={message} />

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                    <div className="flex h-10 items-center gap-2 border-b border-slate-100 px-4 text-[12px] font-semibold uppercase tracking-wide text-slate-600">
                        <ListChecks size={15} />
                        Readiness checks
                    </div>

                    <div className="divide-y divide-slate-100 px-4">
                        <ReadinessRow label="Lock validation" value={lockValidationDisplay} tone={lockValidationDisplay === "Pass" ? "success" : "warning"} />

                        <ReadinessRow label="PTD conditions" value={hasOpenPTD ? `${openPTDCount} open` : openPTDDisplay} tone={hasOpenPTD ? "danger" : "success"} />
                    </div>
                </div>

                <div className="flex h-full flex-col overflow-hidden rounded-lg border border-slate-200 bg-white">
                    <div className="flex h-10 items-center gap-2 border-b border-slate-100 px-4 text-[12px] font-semibold uppercase tracking-wide text-slate-600">
                        <Flag size={16} />
                        Action
                    </div>

                    {decision?.isSupported ? (
                        <div className="flex flex-1 flex-col px-4 py-3">
                            <p className="text-[12px] leading-5 text-slate-700">
                                {isSubmitForFinalReviewOnly
                                    ? "This action returns the loan to "
                                    : decision.milestoneAction === "reopen"
                                        ? "Approving generates the configured documents and returns the loan to "
                                        : "Approving records the decision and advances the loan to "}
                                <span className="font-semibold text-slate-900">{decision.targetMilestone}</span>.
                            </p>

                            <div className="mt-auto grid grid-cols-2 gap-4 pt-3">
                                {!decision.disablePreview && (
                                    <button
                                        type="button"
                                        className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-[12px] font-semibold text-slate-700 shadow-sm transition hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                                        onClick={onPreview}
                                        disabled={processing || loanLocked}
                                    >
                                        <Eye size={16} />
                                        Preview
                                    </button>
                                )}

                                <button
                                    type="button"
                                    className="col-start-2 inline-flex h-10 items-center justify-center rounded-lg bg-[#2080C3] px-4 text-[12px] font-semibold text-white shadow-sm transition hover:bg-[#176da9] disabled:cursor-not-allowed disabled:opacity-50"
                                    onClick={onApprove}
                                    disabled={decision.disableApprove || processing || loanLocked}
                                >
                                    {isSubmitForFinalReviewOnly ? "Submit for final review" : "Approve"}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="px-4 py-3">
                            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
                                {decision?.message || "No underwriting action is available for this scenario."}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {processing && <WorkflowProcessing progress={progress} loadingText={loadingText} />}
        </div>
    );
}

function AccountManagerWorkflow({
    personaName,
    currentMilestone,
    decision,
    lockValidationDisplay,
    processing,
    progress,
    loadingText,
    loanLocked,
    conditionReviewApproved,
    onSubmitConditionReview,
    onSubmitFinalReview,
    onUpgradeToFinal
}) {
    const message = `You're signed in with the ${personaName || "Account Manager"} persona. This loan is at ${currentMilestone || "the current milestone"
        }. Choose where to route it next.`;

    const isUpgradeToFinalWorkflow =
        normalizeMilestoneName(currentMilestone) === MILESTONE_SUBMIT_FOR_CONDITION_REVIEW &&
        decision?.isSupported &&
        decision?.workflowType === WORKFLOW_TYPES.ACCOUNT_MANAGER;

    return (
        <div className="mt-2 space-y-3">
            <PersonaMessage icon={<UserRound size={18} />} message={message} />

            {isUpgradeToFinalWorkflow ? (
                <div className="grid grid-cols-2 gap-3">
                    {lockValidationDisplay === "Needs Validation" ? (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 pt-4 pb-3">
                            <h2 className="text-[15px] font-semibold text-amber-900">Lock Validation Required</h2>

                            <p className="mt-1.5 mb-0 min-h-[40px] text-[13px] leading-5 text-amber-800">
                                The loan requires lock validation before it can be submitted for final review.
                            </p>
                        </div>
                    ) : (
                        <div aria-hidden="true" />
                    )}

                    <div className="rounded-lg border border-gray-200 bg-white px-4 pt-4 pb-3">
                        <h2 className="text-[15px] font-semibold text-gray-900">Upgrade to Final Review</h2>

                        <p className="mt-1.5 mb-0 min-h-[40px] text-[13px] leading-5 text-gray-600">
                            All required conditions are received. Route the loan for the final approval decision.
                        </p>

                        <button
                            type="button"
                            className="mt-3 inline-flex h-10 w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 text-[13px] font-semibold text-gray-800 transition hover:border-blue-400 hover:bg-blue-50 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400 disabled:opacity-100"
                            disabled={processing || loanLocked || decision.disableApprove || lockValidationDisplay === "Needs Validation"}
                            onClick={onUpgradeToFinal}
                        >
                            Submit
                            <ArrowRight size={16} />
                        </button>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <AccountManagerActionCard
                        icon={<ListChecks size={18} />}
                        title="Submit for Condition Review"
                        description="Route the loan to underwriting to review and clear outstanding conditions."
                        approved={conditionReviewApproved}
                        disabled={processing || loanLocked || conditionReviewApproved}
                        onClick={onSubmitConditionReview}
                    />

                    <AccountManagerActionCard
                        icon={<Flag size={18} />}
                        title="Submit for final review"
                        description="All required conditions are received. Route the loan for the final approval decision."
                        disabled={processing || loanLocked}
                        onClick={onSubmitFinalReview}
                    />
                </div>
            )}

            {processing && <WorkflowProcessing progress={progress} loadingText={loadingText} />}
        </div>
    );
}

function AccountManagerActionCard({ icon, title, description, approved = false, disabled, onClick }) {
    return (
        <div className="rounded-lg border border-gray-200 bg-white px-4 pt-4 pb-3">
            <div className="mb-2 text-[#2080C3]">{icon}</div>

            <div className="flex items-center justify-between gap-3">
                <h3 className="text-[15px] font-semibold text-gray-900">{title}</h3>
            </div>

            <p className="mt-1.5 mb-0 min-h-[40px] text-[13px] leading-5 text-gray-600">{description}</p>

            <button
                type="button"
                className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 text-[13px] font-semibold text-gray-800 transition hover:border-blue-400 hover:bg-blue-50 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400 disabled:opacity-100"
                onClick={onClick}
            >
                Submit
                <ArrowRight size={16} />
            </button>
        </div>
    );
}

function PersonaMessage({ icon, message }) {
    return (
        <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-5 py-4 text-sm leading-6 text-blue-800">
            <span className="mt-0.5 shrink-0 text-blue-600">{icon}</span>

            <span>{message}</span>
        </div>
    );
}

function WorkflowProcessing({ progress, loadingText }) {
    const safeProgress = Math.min(100, Math.max(0, progress || 0));

    return (
        <div className="loan-approval-progress rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 to-slate-50 px-5 py-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between gap-4">
                <div className="flex min-w-0 items-center gap-3">
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-[#2080C3] shadow-sm ring-1 ring-blue-200">
                        <RefreshCw size={16} className="animate-spin" />
                    </span>

                    <div className="min-w-0">
                        <div className="text-[12px] font-semibold uppercase tracking-wide text-[#2080C3]">Processing</div>

                        <div className="truncate text-[13px] font-medium text-slate-700">{loadingText || "Processing loan approval workflow..."}</div>
                    </div>
                </div>

                <span className="inline-flex min-w-[52px] shrink-0 items-center justify-center rounded-full border border-blue-200 bg-white px-3 py-1 text-[12px] font-bold text-[#2080C3] shadow-sm">
                    {safeProgress}%
                </span>
            </div>

            <div
                className="h-2.5 overflow-hidden rounded-full bg-blue-100 ring-1 ring-inset ring-blue-200"
                role="progressbar"
                aria-label="Loan approval workflow progress"
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow={safeProgress}
            >
                <div className="loan-approval-progress-fill relative h-full rounded-full transition-all duration-500 ease-out" style={{ width: `${safeProgress}%` }} />
            </div>

            <div className="mt-2 flex items-center justify-between text-[10px] font-medium text-slate-400">
                <span>Started</span>
                <span>{safeProgress === 100 ? "Completed" : "Please do not close this page"}</span>
            </div>
        </div>
    );
}

function VersionBadge({ isCurrent }) {
    return (
        <span
            className={[
                "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[13px] font-medium",
                isCurrent ? "border-blue-200 bg-blue-50 text-[#2080C3]" : "border-slate-200 bg-slate-100 text-slate-500"
            ].join(" ")}
        >
            {isCurrent ? <CheckCircle2 size={20} className="text-[#2080C3]" /> : <History size={20} className="text-slate-400" />}
            {isCurrent ? "Current" : "Previous"}
        </span>
    );
}

function ReadinessRow({ label, value, tone }) {
    const badgeClasses = {
        success: "bg-green-100 text-green-700",
        warning: "bg-amber-100 text-amber-800",
        danger: "bg-red-100 text-red-700"
    };

    return (
        <div className="flex min-h-[42px] items-center justify-between gap-4 py-2">
            <span className="text-[12px] font-medium text-slate-700">{label}</span>

            <span className={["rounded-full px-2.5 py-0.5 text-[12px] font-semibold", badgeClasses[tone] || "bg-slate-100 text-slate-700"].join(" ")}>{value || "-"}</span>
        </div>
    );
}

function MilestoneProgressBar({ currentMilestone, targetMilestone, milestoneOrder = MILESTONE_PROGRESS_ORDER }) {
    const normalizedCurrent = normalizeMilestoneName(currentMilestone);
    const normalizedTarget = normalizeMilestoneName(targetMilestone);

    const currentIndex = milestoneOrder.findIndex(m => m.toLowerCase() === normalizedCurrent.toLowerCase());
    const targetIndex = milestoneOrder.findIndex(m => m.toLowerCase() === normalizedTarget.toLowerCase());
    const hasPostCtcMilestone = milestoneOrder.length > MILESTONE_PROGRESS_ORDER.length;

    return (
        <div className="w-full overflow-x-auto pb-3 pt-2">
            <div className="mx-auto flex w-full min-w-[1180px] max-w-[1280px] items-start justify-center px-1">
                {milestoneOrder.map((milestone, index) => {
                    const isCurrent = index === currentIndex;

                    const isNext = targetIndex >= 0 && targetIndex !== currentIndex && index === targetIndex;

                    const isCompleted = currentIndex >= 0 && index <= currentIndex && !isNext;
                    const isLast = index === milestoneOrder.length - 1;

                    return (
                        <div key={milestone} className="flex min-w-0 flex-1 items-start">
                            <div className={["shrink-0 text-center", hasPostCtcMilestone ? "w-[145px]" : "w-[175px]"].join(" ")}>
                                <div className="flex justify-center">
                                    <div
                                        className={[
                                            "relative flex h-9 w-9 items-center justify-center rounded-full border-2 transition-all duration-300",
                                            isCompleted
                                                ? "border-emerald-500 bg-emerald-500 text-white shadow-sm"
                                                : isNext
                                                    ? "border-[#2080C3] bg-white text-[#2080C3] shadow-[0_0_0_5px_rgba(32,128,195,0.12)]"
                                                    : "border-slate-200 bg-white text-slate-300"
                                        ].join(" ")}
                                    >
                                        {isCompleted ? <Check size={16} strokeWidth={3} /> : <span className="h-2.5 w-2.5 rounded-full bg-current" />}

                                        {isNext && <span className="pointer-events-none absolute inset-0 animate-ping rounded-full bg-[#2080C3]/25" />}
                                    </div>
                                </div>

                                <div
                                    className={[
                                        "mt-2 whitespace-nowrap leading-4",
                                        hasPostCtcMilestone ? "text-[11px]" : "text-[12px]",
                                        isCurrent
                                            ? "font-semibold text-slate-900"
                                            : isNext
                                                ? "font-semibold text-[#2080C3]"
                                                : isCompleted
                                                    ? "font-medium text-slate-600"
                                                    : "text-slate-400"
                                    ].join(" ")}
                                >
                                    {milestone}
                                </div>
                            </div>

                            {!isLast && (
                                <div
                                    className={[
                                        "relative mt-[17px] h-[3px] flex-1 overflow-hidden rounded-full bg-slate-200",
                                        hasPostCtcMilestone ? "mx-1 min-w-[36px]" : "mx-2 min-w-[40px]"
                                    ].join(" ")}
                                >
                                    <div
                                        className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-500"
                                        style={{
                                            width: isCompleted ? "100%" : isCurrent ? "35%" : "0%"
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}